import React, { createContext, useContext } from 'react';
import { create } from 'zustand';

import {
  DeletedEmailEntries,
  RefreshedEmailEntryData,
  SocketData,
  UpdatedEmailEntries,
} from '../types/SocketData';

export type FileTagsMap = { [fileId: string]: any[] };

type InboxStore = {
  isComposeEmailModalActive: boolean;
  isTransactionsEnabled: boolean | null;
  isTreatmentHidden: boolean | null;
  hasComposeEmailDraft: boolean;
  setIsTransactionsEnabled: (isTransactionsEnabled: boolean) => void;
  setIsComposeEmailModalActive: (isComposeEmailModalActive: boolean) => void;
  setHasComposeEmailDraft: (hasComposeEmailDraft: boolean) => void;
  setIsMarkReadClicked: (isMarkReadClicked: boolean) => void;
  isMarkReadClicked: boolean;
  setIsMarkAsMiscellaneousClicked: (isMarkAsMiscellaneousClicked: boolean) => void;
  isMarkAsMiscellaneousClicked: boolean;
  isMarkArchivedClicked: boolean;
  setIsMarkArchivedClicked: (isMarkArchivedClicked: boolean) => void;

  // Sockets
  socketData: SocketData;
  showStaleEntryToast: boolean;
  deletedEmailEntries: DeletedEmailEntries;
  updatedEmailEntries: UpdatedEmailEntries;
  deletedFileEntry: number;
  setDeletedFileEntry: (inboxEntryId: number) => void;
  upsertDeletedEmailEntry: (emailId: number) => void;
  removeDeletedEmailEntryById: (emailId: number) => void;
  upsertUpdatedEmailEntry: (refreshedEmailEntryData: RefreshedEmailEntryData) => void;
  removeUpdatedEmailEntryById: (emailId: number) => void;
  setShowStaleEntryToast: (show: boolean) => void;
  setSocketData: (socketData: SocketData) => void;
  clearSocket: () => void;

  // File Tags
  fileTags: FileTagsMap;
  setFileTags: (fileId: string, tags: any[]) => void;
  updateFileTags: (fileId: string, tags: any[]) => void;
};

const InboxContext = createContext<InboxStore>(undefined);

const useInboxStore = create<InboxStore>(
  (set): InboxStore => ({
    isComposeEmailModalActive: false,
    isTransactionsEnabled: null,
    isTreatmentHidden: false,
    hasComposeEmailDraft: false,
    showStaleEntryToast: false,
    socketData: null,
    deletedEmailEntries: null,
    updatedEmailEntries: null,
    deletedFileEntry: null,
    isMarkReadClicked: false,
    isMarkAsMiscellaneousClicked: false,
    isMarkArchivedClicked: false,
    fileTags: {},

    setIsTransactionsEnabled: (isTransactionsEnabled: boolean) =>
      set(() => ({
        isTransactionsEnabled: isTransactionsEnabled,
      })),
    setSocketData: (socketData: SocketData) =>
      set(() => {
        return { socketData: socketData };
      }),
    clearSocket: () =>
      set(() => {
        return {
          showStaleEntryToast: false,
          socketData: null,
          deletedEmailEntries: null,
          updatedEmailEntries: null,
          deletedFileEntry: null,
        };
      }),
    setShowStaleEntryToast: (show: boolean) =>
      set(() => ({
        showStaleEntryToast: show,
      })),
    setDeletedFileEntry: (inboxEntryId: number) =>
      set(() => ({
        deletedFileEntry: inboxEntryId,
      })),
    upsertDeletedEmailEntry: (emailId: number) =>
      set((state) => {
        const newUpdatedEmailEntries = state.updatedEmailEntries
          ? { ...state.updatedEmailEntries }
          : null;
        if (state.updatedEmailEntries && state.updatedEmailEntries[emailId]) {
          // If there's existing update for this email, we cancel it
          delete newUpdatedEmailEntries[emailId];
        }

        return {
          deletedEmailEntries: {
            ...(state.deletedEmailEntries ? state.deletedEmailEntries : {}),
            [emailId]: true,
          },
          updatedEmailEntries: newUpdatedEmailEntries,
        };
      }),
    removeDeletedEmailEntryById: (emailId: number) =>
      set((state) => {
        const newDeletedEmailEntries = { ...state.deletedEmailEntries };
        delete newDeletedEmailEntries[emailId];
        return { deletedEmailEntries: newDeletedEmailEntries };
      }),
    upsertUpdatedEmailEntry: (refreshedEmailEntryData: RefreshedEmailEntryData) =>
      set((state) => {
        const newDeletedEmailEntries = state.deletedEmailEntries
          ? { ...state.deletedEmailEntries }
          : null;

        // Edge case of delete -> restored vice versa
        if (
          state.deletedEmailEntries &&
          state.deletedEmailEntries[refreshedEmailEntryData.rawEmailEntry.id]
        ) {
          // We can assume this is an inverse action trash <->
          // Remove from deleted {} since it has been trashed/restored
          delete newDeletedEmailEntries[refreshedEmailEntryData.rawEmailEntry.id];
        }

        return {
          updatedEmailEntries: {
            ...(state.updatedEmailEntries ? state.updatedEmailEntries : {}),
            [refreshedEmailEntryData.rawEmailEntry.id]: refreshedEmailEntryData,
          },
          deletedEmailEntries: newDeletedEmailEntries,
        };
      }),
    removeUpdatedEmailEntryById: (emailId: number) =>
      set((state) => {
        const newUpdatedEmailEntries = { ...state.updatedEmailEntries };
        delete newUpdatedEmailEntries[emailId];
        return { updatedEmailEntries: newUpdatedEmailEntries };
      }),
    setIsComposeEmailModalActive: (isComposeEmailModalActive: boolean) =>
      set(() => {
        return { isComposeEmailModalActive };
      }),
    setHasComposeEmailDraft: (hasComposeEmailDraft: boolean) =>
      set(() => {
        return { hasComposeEmailDraft };
      }),
    setIsMarkReadClicked: (isMarkReadClicked: boolean) =>
      set((state) => ({
        ...state,
        isMarkReadClicked,
      })),
    setIsMarkAsMiscellaneousClicked: (isMarkAsMiscellaneousClicked: boolean) =>
      set(() => ({
        isMarkAsMiscellaneousClicked: isMarkAsMiscellaneousClicked,
      })),
    setIsMarkArchivedClicked: (isMarkArchivedClicked: boolean) =>
      set(() => ({
        isMarkArchivedClicked: isMarkArchivedClicked,
      })),
    setFileTags: (fileId, tags) =>
      set((state) => ({ fileTags: { ...state.fileTags, [fileId]: tags } })),
    updateFileTags: (fileId, tags) =>
      set((state) => ({ fileTags: { ...state.fileTags, [fileId]: tags } })),
  })
);

export const InboxProvider = ({ isTreatmentHidden = false, children }) => {
  const store = useInboxStore();

  return (
    <InboxContext.Provider
      value={{
        ...store,
        isTreatmentHidden,
      }}
    >
      {children}
    </InboxContext.Provider>
  );
};

export function useInbox() {
  const store = useContext(InboxContext);
  if (!store) {
    throw new Error('useInbox must be used within a InboxProvider');
  }
  return {
    ...store,
  };
}
